import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Droplets, Wallet, Plus, Minus } from 'lucide-react';
import { LaunchPool } from './components/LaunchPool';
import { AddLiquidity } from './components/AddLiquidity';
import { RemoveLiquidity } from './components/RemoveLiquidity';
import { MyPositions } from './components/MyPositions';

export default function LiquidityPage() {
  const [activeTab, setActiveTab] = useState('positions');

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Page Header */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Droplets className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold">Liquidity Management</h1>
          <Droplets className="h-8 w-8 text-primary" />
        </div>
        <p className="text-muted-foreground text-lg mb-6">
          Launch pools, add liquidity, or remove liquidity for your tokens
        </p>
        
        {/* DEX Logos */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8 mt-6 px-4">
          <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
            <img 
              src="/Pancakeswap logo MdbApp.png" 
              alt="PancakeSwap" 
              className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
            />
            <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">PancakeSwap</span>
          </div>
          <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
            <img 
              src="/Uniswap Logo.png" 
              alt="Uniswap" 
              className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
            />
            <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">Uniswap</span>
          </div>
          <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
            <img 
              src="/Trader Joe Logo MdbApp.png" 
              alt="Trader Joe" 
              className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
            />
            <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">Trader Joe</span>
          </div>
          <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
            <img 
              src="/DragonSwap Logo MdbApp.io.png" 
              alt="DragonSwap" 
              className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
            />
            <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">DragonSwap</span>
          </div>
          <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
            <img 
              src="/QuickSwap Logo.png" 
              alt="QuickSwap" 
              className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
            />
            <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">QuickSwap</span>
          </div>
          <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-[60px] sm:min-w-[80px]">
            <img 
              src="/Veledrome V2.png" 
              alt="Velodrome" 
              className="h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 object-contain"
            />
            <span className="text-[10px] sm:text-xs text-muted-foreground font-medium text-center">Velodrome</span>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <Card>
        <CardHeader className="text-center">
          <CardTitle>Manage Your Liquidity</CardTitle>
          <CardDescription>
            Choose an action below to manage liquidity for your tokens
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="positions" className="flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                <span>My Positions</span>
              </TabsTrigger>
              <TabsTrigger value="launch" className="flex items-center gap-2">
                <Droplets className="h-4 w-4" />
                <span>Launch Pool</span>
              </TabsTrigger>
              <TabsTrigger value="add" className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                <span>Add Liquidity</span>
              </TabsTrigger>
              <TabsTrigger value="remove" className="flex items-center gap-2">
                <Minus className="h-4 w-4" />
                <span>Remove Liquidity</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="positions" className="space-y-4">
              <MyPositions
                onLaunchPool={() => setActiveTab('launch')}
                onAddLiquidity={() => setActiveTab('add')}
              />
            </TabsContent>

            <TabsContent value="launch" className="space-y-4">
              <LaunchPool />
            </TabsContent>

            <TabsContent value="add" className="space-y-4">
              <AddLiquidity />
            </TabsContent>

            <TabsContent value="remove" className="space-y-4">
              <RemoveLiquidity />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Droplets className="h-4 w-4 text-primary" />
              Launch Pool
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Create a new liquidity pool for your token on supported DEXs. This is the first step for enabling trading.
            </p>
          </CardContent>
        </Card>

        <Card className="border-green-500/20">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4 text-green-500" />
              Add Liquidity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Add more liquidity to an existing pool to increase trading depth and earn fees from trades.
            </p>
          </CardContent>
        </Card>

        <Card className="border-red-500/20">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Minus className="h-4 w-4 text-red-500" />
              Remove Liquidity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Remove your liquidity from a pool to retrieve your tokens and any earned fees.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
